<template>
  <div id="login-stage-wrap">

    <!-- Stage 1: Email Verification -->
    <div v-if="stage === 1" class="stage" @keyup.enter="verifyEmail">
      <div>
        <span v-html="modalMessage"></span>
        <Input type="email" label="Email" name="user_email" v-model="user_email" id="emailInput" autocomplete="email" />
        <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
      </div>
      <div class="button-group">
        <input type="button" @click="verifyEmail" class="fa-btn fa-btn-primary fa-btn-modal" value="Continue">
      </div>
    </div>

    <!-- Stage 2: Login Password -->
    <div v-if="stage === 2" id="login-grid-wrap">
      <div id="login-left">
        <span v-html="modalMessage"></span>
        <form @submit.prevent="login">
          <Input type="email" name="email" id="user_email_verified" label="Email" v-model="user_email" :readonly="true" autocomplete="email"/>
          <Input type="password" label="Password" v-model="user_password" name="password" id="passwordInput" autocomplete="current-password"/>
          <ModalMessage :message="feedbackMessage" :type="feedbackType" style="margin-top: 1rem;"/>
          <input type="submit" class="fa-btn fa-btn-primary fa-btn-modal" value="Sign in">
          <input type="checkbox" name="login_modular" v-model="modular_variable" style="display:none !important" tabindex="-1" autocomplete="off">
          <a class="hint" href="javascript:" v-on:click="uiStore.setModalType('forgot', user_email)">Forgot password?</a>
          <a @click="goBackToLogin" class="go-back-link">Go back</a>
        </form>
      </div>
    </div>

    <!-- Stage 2: Account Details -->
    <div v-if="stage === 3" class="stage" @keyup.enter="completeSignup">
      <h2>Sign up</h2>
      <div class="stage-content">
        <Input type="text" name="firstname" label="First name" v-model="first_name" autocomplete="given-name"/>
        <Input type="text" name="lastname" label="Last name" v-model="last_name"  autocomplete="family-name"/>
        <Input type="password_strength" name="password" label="Password" v-model="password" autocomplete="new-password"/>
        <div class="password-suggestion-box" v-if="showPasswordSuggestions">
          <strong>Password Tips:</strong>
          <ul>
            <li>At least 8 characters long</li>
            <li>Contains both upper and lower case characters</li>
            <li>Includes at least one number and one special character</li>
            <li>Is not a word found in a dictionary, a name, or an easily guessable combination</li>
          </ul>
        </div>
        <div class="checkbox-container" style="margin-top: 1rem;">
          <input 
            id="newsletter" 
            v-model="newsletter_accepted" 
            type="checkbox"
            :disabled="isNewsletterMandatory"
            :checked="isNewsletterMandatory"
          >
          <label for="newsletter">
            <template v-if="isNewsletterMandatory">
              Subscription to the Newsletter is mandatory to download the free product.
            </template>
            <template v-else>
              I'd like to receive the Newsletter
            </template>
          </label>
        </div>
        <div class="checkbox-container">
          <input id="terms" name="terms_conditions_checkbox" v-model="terms_accepted" type="checkbox" required>
          <label for="terms">I accept the <NuxtLink to="/terms-conditions">Terms & Conditions</NuxtLink></label>
        </div>
        <ModalMessage :message="feedbackMessage" :type="feedbackType" />
      </div>
      <div class="button-group">
        <input type="button" @click="completeSignup" class="fa-btn fa-btn-primary fa-btn-modal" value="Sign up">
        <a @click="goBackToLogin" class="go-back-link">Go back</a>
      </div>
    </div>

    <!-- Stage 4: Activation Link Sent -->
    <div v-if="stage === 4" class="stage">
      <h2>Activation Link Sent</h2>
      <div class="stage-content">
        <p>We've sent an activation link to your email address: <strong>{{ user_email }}</strong>.<br><br> Please check your inbox and click on the link to activate your account. Afterwards you can login for the first time.<br><br></p>
        <ModalMessage :message="feedbackMessage" :type="feedbackType" />
      </div>
    </div>
  </div>

  <!-- Stage 5: Activation Link Sent -->
  <div v-if="stage === 5" class="stage">
      <h2>Inactive Account detected</h2>
      <div class="stage-content">
          <p>We've noticed that you previously attempted to create an account with your email {{ user_email }}, but it's not yet activated.<br><br> To complete your registration, <b>please click the activation link</b> in you should have received in your <b>email</b>.<br><br>If you did not receive the email or cannot find it, you can resend the activation email by clicking the button below.<br><br></p>

          <input type="button" @click="resendActivation" :disabled="resendCooldown > 0" class="fa-btn fa-btn-primary fa-btn-modal" value="Resend Activation Email">
          <p v-if="resendCooldown > 0" class="reactivation-message">Activation mail successfully sent. Please click the activation link in the mail. You need to wait {{ resendCooldown }} seconds before you can send another activation email.</p>
          <ModalMessage :message="feedbackMessage" :type="feedbackType" />
      </div>
  </div>


</template>


<script setup lang="ts">
import { ref, computed, onBeforeMount, onUnmounted, onMounted, watch, nextTick } from 'vue';
import { MessageType } from '@/types/enums.ts';
import APIClient from '~/api/apiClient';
import { useUIStore } from '@/store/uiStore';
import { ModalTypes } from '@/types/enums';
import { CSRFType } from '@/api/apiClient';
import CSRFManager from '~/utils/csrfManager.ts';
import { useUserStore } from '~/store/userStore';
import { UserCredentials } from '@/api/apiClient';
import useGTag from '~/composables/useGTag';
import { useDataStore } from '~/store/dataStore'; // Import the dataStore
import CookieManager from '~/utils/cookieManager.ts';

const { 
  trackSignUp, trackLogin
} = useGTag();

const userStore = useUserStore();
const dataStore = useDataStore();

const csrfManager = ref<CSRFManager | null>(null);
const csrfToken = computed(() => csrfManager.value?.state.csrfToken);
const modalMessage = computed(() => uiStore.modalMessage);

const apiClient = APIClient.getInstance();

const validateEmail = (email) => {
    const mailformat = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+$/;
    return mailformat.test(email);
}


onBeforeMount(async () => {
 /* if(userStore.userLoginMail !== null)
  {
    user_email.value = userStore.userLoginMail; 
  }*/
});

const isLoggedIn =  computed(() => userStore.userData?.is_logged_in || false);

onMounted(() => {

  const params = new URLSearchParams(window.location.search);
  const idParam = params.get('id');
  const keyParam = params.get('key');

  if (idParam && keyParam) 
  {    

    (async () => {
      try {
        const response = await apiClient.verifyEmail(idParam, keyParam);
        console.log("adf verifyResponse", response);
        if(response.result.valid && response.result?.email && validateEmail(response.result?.email))
        {
          user_email.value = response.result?.email
          proceedToLogin();
        }
        else
        {
          console.log("verifyMail error. User was probably already signed up.", response);
        }
      } catch (error) {
        console.log("verifyMail error", error);
      }
    })();

  }

   const loadedMail = CookieManager.getInstance().getCookie(userMailCookieName);
   if(loadedMail?.length != 0 && validateEmail(loadedMail))
   {
      user_email.value = loadedMail;
      console.log("user mail loaded", user_email.value);
   }
   else
   {
      // Ensure the element is present before trying to focus
      const emailInput = document.getElementById('emailInput'); // Make sure your input has this ID
      if (emailInput) {
        emailInput.focus();
      }
   }
});

onUnmounted(() => {
  // Check if csrfManager has been initialized before calling stopFetchingToken
  if (csrfManager.value !== null) {
    csrfManager.value.stopFetchingToken();
  }
});

const uiStore = useUIStore();
const stage = ref(1); 

const user_email = ref('');
const first_name = ref('');
const last_name = ref('');
const password = ref('');
const showPasswordSuggestions = ref(false);

const userMailCookieName = "userMail";

const newsletter_accepted = ref(false);

const passwordInput = ref(null); // Ref for your input element

// Watcher to focus the password input when stage is 2
watch(stage, async (newStage) => {
  if (newStage === 2) {
    await nextTick(); // Wait for the DOM to update
    // Check if the ref exists
    if (passwordInput.value) {
      // If using a custom Input component that forwards the ref to the native input
      if (passwordInput.value.$el) {
        passwordInput.value.$el.focus();
      } else {
        // Direct focus if it's a native input ref
        passwordInput.value.focus();
      }
    }
  }
});


// Determine if the newsletter subscription is mandatory
const isNewsletterMandatory = computed(() => {
  return modalMessage.value?.includes("to Forward Audio to download");
});

// Automatically check and disable the checkbox if the newsletter is mandatory
if (isNewsletterMandatory.value) {
  newsletter_accepted.value = true;
}

const terms_accepted = ref(false);

const initialMessage = 'Please enter your mail to sign in or to create a new account...';
const initialMessageStageTwo = 'Please enter your password to continue...';
const feedbackMessage = ref(initialMessage);
const feedbackType = ref(MessageType.INFO);

const areNamesFilled = computed(() => first_name.value && last_name.value);

function resetFeedback() {
  feedbackMessage.value = '';
  feedbackType.value = MessageType.INFO;
}

const user_password = ref("");
const modular_variable = ref('');  // Honeypot for bots.

// Updated to be an async function
async function initializeCSRFManager(type: CSRFType) {
  csrfManager.value = new CSRFManager(type);
  // Await the async call
  await csrfManager.value.startFetchingToken();
}

async function proceedToLogin() {
  await initializeCSRFManager(CSRFType.USER_LOGIN);
  stage.value = 2; // Transition to login stage
  
  feedbackMessage.value = initialMessageStageTwo;
  feedbackType.value = MessageType.INFO;

  // Wait for the next DOM update cycle after changing the stage
  await nextTick();

  // Ensure the element is present before trying to focus
  const passwordInput = document.getElementById('passwordInput'); // Make sure your input has this ID
  if (passwordInput) {
    passwordInput.focus();
  }
}

async function proceedToRegistration() {
  await initializeCSRFManager(CSRFType.USER_REGISTER);
  stage.value = 3; // Transition to registration stage
}

async function verifyEmail() {
  console.log("Verifying email...", user_email.value);

  // Eliminate trailing whitespaces
  user_email.value = user_email.value.trim();

  if(!validateEmail(user_email.value)){
    feedbackMessage.value = "Please enter a valid email address.";
    feedbackType.value = MessageType.ERROR;
    return;  
  }

  try {
    const response = await apiClient.isValidEmail(user_email.value);
    console.log("Email verification response:", response);

    if (response) {
      resetFeedback();
       console.log("adf response", response);
      if(response.result.error === "used") {
        await proceedToLogin();
        CookieManager.getInstance().setPreferenceCookie(userMailCookieName, user_email.value);
      }      
      else if(response.result.error === "format") {
        feedbackMessage.value = "Please enter a valid email address.";
        feedbackType.value = MessageType.ERROR;
      }
      else if(response.result.error === "dnsmx") {
        feedbackMessage.value = "The email address cannot be verified. Please check if it's correct or try a different one.";
        feedbackType.value = MessageType.ERROR;
      }
      else if(response.result.error === "trashmail") {
          feedbackMessage.value = "The email address provided is not allowed. Please use a permanent email address.";
          feedbackType.value = MessageType.ERROR;
      }
      else if(response.result.error === "inactive") {
        CookieManager.getInstance().setPreferenceCookie(userMailCookieName, user_email.value);
        stage.value = 5;
        resetFeedback();
      }
      else
      {
        await proceedToRegistration();
        CookieManager.getInstance().setPreferenceCookie(userMailCookieName, user_email.value);
      }
    } else {
      console.log("email validate response", response);
      feedbackMessage.value = response.error || 'There was an error checking your email. Please try again later.';
      feedbackType.value = MessageType.ERROR;
    }
  } catch (error) {
    // Handle any exceptions like network errors, timeouts, etc.
    feedbackMessage.value = 'Error verifying email. Please try again later.';
    feedbackType.value = MessageType.ERROR;
  }
}

const login = async () => {
  console.log(csrfManager, "csrfManager");
  console.log('Login...');
  if(csrfManager.value)
    await csrfManager.value?.fetchToken();
  else
  {
    await initializeCSRFManager(CSRFType.USER_LOGIN);
  }

  if (modular_variable.value) {
    console.log('Possible bot detected.');
    return;
  }
  console.log("csrfToken", csrfToken.value);

 const credentials: UserCredentials = {
    email_address: user_email.value,
    password: user_password.value
  };

  const response = await apiClient.loginUser(credentials, csrfToken.value);
  console.log("Login response: ", response);

  if (response.valid && response.user) {
      console.log('Logged in successfully!', response.result);
      trackLogin();
      userStore.setUserData(response.user);
      uiStore.setModalType(ModalTypes.INACTIVE);
      await dataStore.fetchProductData(); 
  } else if (response.login_error) {
      console.log('Login error:', response.login_error);
      if(csrfManager.value)
        csrfManager.value?.fetchToken();
      else
      {
        await initializeCSRFManager(CSRFType.USER_LOGIN);
      }

      feedbackMessage.value = response.login_error;
  } else {
      console.error('Login failed:', response);
      userStore.setUserLoginMail(credentials.email_address);
      if(csrfManager.value)
        csrfManager.value?.fetchToken();
      else
      {
        await initializeCSRFManager(CSRFType.USER_LOGIN);
      }
      feedbackType.value = MessageType.ERROR;
      if(response.code === 500)
      {
        feedbackMessage.value = "Login failed. Ensure that your email and password are correct.";
      }
      else
      {
        feedbackMessage.value = response.error;
      }
  }

};

function goBackToLogin() {
  resetFeedback();
  stage.value = 1;
  feedbackMessage.value = initialMessage;
}

async function completeSignup() {

  if (!areNamesFilled.value) {
      feedbackMessage.value = 'Please fill in your name fields.';
      feedbackType.value = MessageType.ERROR;
      return;
  }
  if(password.value.length < 8) {
      feedbackMessage.value = 'Please enter a password that is at least 8 characters long.';
      feedbackType.value = MessageType.ERROR;
      showPasswordSuggestions.value = true;
      return;
  }
  if (!terms_accepted.value) {
    feedbackMessage.value = 'You must agree to the terms and conditions.';
    feedbackType.value = MessageType.ERROR;
    return;
  }

  const registrationData = {
    csrf_token: csrfToken.value,
    email_address: user_email.value,
    first_name: first_name.value,
    last_name: last_name.value,
    password: password.value,
    password_2: password.value,
    subscribe_to_newsletter: newsletter_accepted.value,
    accepted_tos: true
  };

  // Call the API to register the user
  const response = await apiClient.registerUser(registrationData);
  console.log("Registration response: ", response);

  // Handle the API response
  if (response && response.valid) { // Adjust based on the structure of your APIResponse
    feedbackMessage.value = 'Signup successful! Please activate your account from the link sent to your email.';
    feedbackType.value = MessageType.SUCCESS;
    trackSignUp();
    stage.value = 4; // Move to the 4th stage
  } else {
    if(response.error?.includes("invalid csrf token"))
    {
      feedbackMessage.value = "Something went wrong. Please press 'Sign up' again!";
      await initializeCSRFManager(CSRFType.USER_REGISTER);
    }
    else
      feedbackMessage.value = response.error || 'Signup failed!';
    feedbackType.value = MessageType.ERROR;
    await initializeCSRFManager(CSRFType.USER_REGISTER);
  }
}

const resendCooldown = ref(0);

function startCooldown() {
  resendCooldown.value = 60;
  const interval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value === 0){
      clearInterval(interval);
      goBackToLogin();
    } 
  }, 1000);
}

async function resendActivation() {
  console.log("Resending email verification...", user_email.value);

  if(!validateEmail(user_email.value)){
    feedbackMessage.value = "Please enter a valid email address.";
    feedbackType.value = MessageType.ERROR;
    return;  
  }

  try {
    const response = await apiClient.resendActivationEmail(user_email.value);
    console.log("Email resend activation response:", response);
    if(response.valid)
    {
      startCooldown();
    }
  }
  catch
  {

  }
}


</script>

<style lang="scss" scoped>
@import '~/assets/constants.scss';

#login-stage-wrap {
  // Ensure consistent size throughout the stages
  //min-height: 700px;
}

#sign-up-login {
    margin-top: 1rem;
    padding-top: 1rem;
    display: grid;
    align-content: center;
    justify-content: center;
    border-top: 1px solid $fa-orange;
  }

.password-suggestion-box {
  border: 1px solid $fa-orange;
  padding: 10px;
  font-size: 0.75rem;
  border-radius: $fa-border-radius;
  margin: 1rem;
  margin-top: 1.25rem;
}

.password-suggestion-box ul {
  list-style-type: disc;
  margin-left: 20px;
}

.password-suggestion-box strong {
  display: block;
  margin-bottom: 5px;
}

.go-back-link {
  display: inline-block;
  margin-top: 10px;
  cursor: pointer;
  text-decoration: underline;
}

.fa-btn {
  margin: 10px 0;
}

#signup-stage-wrap {
  min-height: 300px;
}

.stage {
  display: flex;
  flex-direction: column;
  height: 100%;
  
  h2 {
    flex: 0;
    margin-bottom: 15px; // Add some spacing after the title
  }

  .stage-content {
    flex: 1; // This allows it to take up all available space
    display: flex;
    flex-direction: column;
    justify-content: center; // Center the content vertically
    min-height: 20vh;
    min-width: 50vw;
  }

  .fa-btn {
    flex: 0;
    margin: 10px 0;
  }

  .go-back-link {
    flex: 0;
    display: inline-block;
    margin-top: 10px;
    cursor: pointer;
    text-decoration: underline;
  }
}

// Progress Bar

.progress-bar-container {
    height: 0.55rem;
    background: $light-gray-row-bg; /* Adjust to your theme */
    border-radius: 1rem; 
    margin-bottom: 0.75rem;
}

.progress-bar {
    height: 100%;
    background: #4caf50; /* Adjust to your theme */
    border-radius: 1rem;
    transition: width 0.5s ease;
}

.progress-labels {
    display: flex;
    justify-content: space-between;
}

.progress-labels span {
    font-size: 1rem;
    opacity: 0.7;
    transition: opacity 0.5s ease;
    padding: 0.5rem;
}

.progress-labels span.active {
    opacity: 1;
    font-weight: bold;
}
.reactivation-message
{
  color: $fa-green;
}

</style>